import { useCallback, useRef, useState } from 'react';
import { UploadSimple, X } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

const ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.rtf',
  '.ppt', '.pptx', '.odt', '.ods', '.odp',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tif', '.tiff', '.heic',
].join(',');

export function DocumentUploadModal({ open, onOpenChange, onUpload, uploading, progress, error }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(null);

  const reset = useCallback(() => {
    setSelected(null);
    setDragOver(false);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handleClose = useCallback((next) => {
    if (uploading) return;
    if (!next) reset();
    onOpenChange(next);
  }, [uploading, onOpenChange, reset]);

  const pickFile = useCallback((file) => {
    if (!file || uploading) return;
    setSelected(file);
  }, [uploading]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    pickFile(file);
  }, [pickFile]);

  const handleUpload = useCallback(async () => {
    if (!selected || uploading) return;
    const ok = await onUpload(selected);
    if (ok) reset();
  }, [selected, uploading, onUpload, reset]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-4" data-testid="document-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-sm">Upload document</DialogTitle>
        </DialogHeader>

        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300'
          } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
          data-testid="document-drop-zone"
        >
          <UploadSimple className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-[11px] text-gray-600 font-medium">Drag file here</p>
          <p className="text-[10px] text-gray-400 mt-1">Or browse files</p>
          <p className="text-[9px] text-gray-400 mt-2">PDF, Word, Excel, images, and other business formats (max 25 MB)</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="document-file-input"
          />
        </div>

        {selected && (
          <div className="flex items-center gap-2 text-[10px] bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
            <span className="flex-1 truncate text-gray-700">{selected.name}</span>
            <span className="text-gray-400 shrink-0">{(selected.size / 1024).toFixed(0)} KB</span>
            {!uploading && (
              <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {uploading && (
          <div className="space-y-1" data-testid="document-upload-progress">
            <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[9px] text-gray-500 text-center">Uploading… {progress}%</p>
          </div>
        )}

        {error && (
          <p className="text-[10px] text-red-600" data-testid="document-upload-error">{error}</p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => handleClose(false)}
            disabled={uploading}
            className="text-[10px] px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!selected || uploading}
            className="text-[10px] px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            data-testid="document-upload-submit"
          >
            Upload
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
